import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Prompt, useParams, useHistory } from 'react-router-dom'
import { makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import SydButton from '../../../../../commonDesign/Button'
import Icon from '../../../../../atoms/Icon'
import Loading from '../../../../../molecules/Loading'
import {
  useGetAiSkill,
  useGetAiSkills,
  useInsertAiSkill,
  useGetAiSkillTags,
  useGetAiModels
} from '../../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../../Analysis/SmartEndpoints/formatRelativeTime'
import SkillFormProvider, { useSkillForm } from './SkillFormContext'
import SkillConfigPopover from './SkillConfigPopover'
import SettingsPanel from './SettingsPanel'
import InstructionsPanel from './InstructionsPanel'
import DesignerPanel from './DesignerPanel'

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    margin: 'var(--margin-md) var(--margin-lg) 0',
    borderRadius: 'var(--border-radius-lg)',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--color-neutral-100)',
    flexShrink: 0
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  fieldDescription: {
    fontSize: '10px',
    fontWeight: 400,
    color: 'var(--color-neutral-400)',
    textTransform: 'none',
    letterSpacing: 0,
    marginLeft: '4px'
  },
  fieldInput: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  versionSelect: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    cursor: 'pointer',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    paddingBottom: '2px'
  },
  tagBadgeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '1px'
  },
  tagBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'var(--font-mono, monospace)',
    letterSpacing: '0.2px',
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-500)'
  },
  tagBadgeLive: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  body: {
    display: 'flex',
    flex: 1,
    gap: 'var(--gap-lg)',
    padding: 'var(--padding-md) var(--padding-lg) 0',
    minHeight: 0,
    overflow: 'hidden'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '3px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white'
  },
  leftColumn: {
    flex: 0.8,
    minWidth: '260px'
  },
  middleColumn: {
    flex: 1,
    minWidth: '340px'
  },
  rightColumn: {
    width: '320px',
    flexShrink: 0,
    backgroundColor: 'var(--color-ai-050)',
    borderColor: 'var(--color-ai-200, rgba(169,88,255,0.25))'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-lg)',
    padding: '5px var(--padding-lg)',
    flexShrink: 0,
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  statusDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '4px',
    verticalAlign: 'middle'
  },
  gearBtn: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: 'var(--color-neutral-500)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--color-neutral-700)'
    }
  },
  gearBtnOpen: {
    backgroundColor: 'var(--color-primary-050)',
    borderColor: 'var(--color-primary-300)',
    color: 'var(--color-primary-600)'
  },
  configPopoverAnchor: {
    position: 'relative'
  }
}))

function toArray (val) {
  if (Array.isArray(val)) return val.filter(Boolean)
  if (typeof val === 'string') return val.split('\n').map(l => l.trim()).filter(Boolean)
  return []
}

function SkillWorkspaceInner () {
  const classes = useStyles()
  const { skillId: skillIdParam } = useParams()
  const history = useHistory()
  const isEditing = !!skillIdParam

  const form = useSkillForm()
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [savedState, setSavedState] = useState(null)
  const configRef = useRef(null)
  const hydrated = useRef(false)
  const isDirtyRef = useRef(false)

  // Load the specific version of the skill being edited
  const { data: skillData, isLoading: skillLoading } = useGetAiSkill(
    isEditing ? skillIdParam : null,
    { version: selectedVersion || undefined, enabled: isEditing }
  )

  // Load all versions for the version dropdown
  const { data: versionsData } = useGetAiSkills({
    skillIds: isEditing ? [skillIdParam] : undefined,
    allVersions: true,
    includeAll: true,
    skip: 0,
    take: 1000,
    enabled: isEditing
  })

  const { data: tagsData } = useGetAiSkillTags(isEditing ? skillIdParam : null, { enabled: isEditing })
  const { data: modelsData } = useGetAiModels()
  const insertSkill = useInsertAiSkill()

  const currentSkill = skillData?.skill || null

  const versions = useMemo(() => {
    if (!currentSkill && !versionsData?.skills?.length) return selectedVersion ? [selectedVersion] : [1]
    const fromVersions = versionsData?.skills
      ?.filter(s => s.skillId === skillIdParam)
      ?.map(s => s.version)
    const maxV = fromVersions?.length
      ? Math.max(...fromVersions)
      : (currentSkill?.version || 1)
    return Array.from({ length: maxV }, (_, i) => maxV - i)
  }, [currentSkill, versionsData, skillIdParam, selectedVersion])

  const currentVersion = selectedVersion || (currentSkill?.version) || 1

  const maxVersion = versions[0] || 1
  const tagsForCurrentVersion = useMemo(() => {
    const tags = tagsData?.tags || []
    const result = tags.filter(t => t.version === currentVersion).map(t => t.tagName)
    if (currentVersion === maxVersion && !result.includes('next')) {
      result.push('next')
    }
    return result
  }, [tagsData, currentVersion, maxVersion])

  // Hydrate form from loaded skill
  useEffect(() => {
    if (!isEditing || !currentSkill || hydrated.current) return
    const sc = currentSkill.skillContent || {}
    form.setSkillId(currentSkill.skillId)
    form.setSkillName(currentSkill.skillName || '')
    form.setDescription(sc.description || '')
    form.setUseWhen(toArray(sc.useWhen))
    form.setDontUseWhen(toArray(sc.dontUseWhen))
    form.setInstructions(sc.instructions || '')
    form.setDefaultPrompt(sc.defaultPrompt || '')
    form.setGreetingMessage(sc.greetingMessage || '')
    form.setSkillConfig({
      audience: sc.audience,
      focuses: sc.focuses || ['any'],
      modelId: sc.modelId || null,
      maxIterations: sc.maxIterations || null,
      temperature: sc.temperature ?? null,
      maxTokens: sc.maxTokens ?? null,
      iconName: sc.iconName || 'cube',
      visible: sc.visible !== false
    })

    setSavedState({
      skillName: currentSkill.skillName || '',
      description: sc.description || '',
      useWhen: JSON.stringify(toArray(sc.useWhen)),
      dontUseWhen: JSON.stringify(toArray(sc.dontUseWhen)),
      instructions: sc.instructions || '',
      defaultPrompt: sc.defaultPrompt || '',
      greetingMessage: sc.greetingMessage || '',
      config: JSON.stringify({
        audience: sc.audience,
        focuses: sc.focuses || ['any'],
        modelId: sc.modelId || null,
        maxIterations: sc.maxIterations || null,
        temperature: sc.temperature ?? null,
        maxTokens: sc.maxTokens ?? null,
        iconName: sc.iconName || 'cube',
        visible: sc.visible !== false
      })
    })

    hydrated.current = true
  }, [isEditing, currentSkill, form])

  // Reset hydration when switching versions
  useEffect(() => {
    hydrated.current = false
  }, [selectedVersion])

  const isDirty = useMemo(() => {
    if (!isEditing) {
      return form.skillId.length > 0 || form.skillName.length > 0 || form.hasContent
    }
    if (!savedState) return false
    return (
      form.skillName !== savedState.skillName ||
      form.description !== savedState.description ||
      JSON.stringify(form.useWhen) !== savedState.useWhen ||
      JSON.stringify(form.dontUseWhen) !== savedState.dontUseWhen ||
      form.instructions !== savedState.instructions ||
      form.defaultPrompt !== savedState.defaultPrompt ||
      form.greetingMessage !== savedState.greetingMessage ||
      JSON.stringify(form.skillConfig) !== savedState.config
    )
  }, [isEditing, savedState, form])

  useEffect(() => { isDirtyRef.current = isDirty }, [isDirty])

  const idInvalid = form.skillId.length > 0 && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(form.skillId)
  const hasRequiredFields = form.description.trim().length > 0 &&
    form.useWhen.some(s => s.trim().length > 0) &&
    form.instructions.trim().length > 0

  const handleReset = useCallback(() => {
    if (isEditing && currentSkill) {
      const sc = currentSkill.skillContent || {}
      form.setSkillId(currentSkill.skillId)
      form.setSkillName(currentSkill.skillName || '')
      form.setDescription(sc.description || '')
      form.setUseWhen(toArray(sc.useWhen))
      form.setDontUseWhen(toArray(sc.dontUseWhen))
      form.setInstructions(sc.instructions || '')
      form.setDefaultPrompt(sc.defaultPrompt || '')
      form.setGreetingMessage(sc.greetingMessage || '')
      form.setSkillConfig({
        audience: sc.audience,
        focuses: sc.focuses || ['any'],
        modelId: sc.modelId || null,
        maxIterations: sc.maxIterations || null,
        temperature: sc.temperature ?? null,
        maxTokens: sc.maxTokens ?? null,
        iconName: sc.iconName || 'cube',
        visible: sc.visible !== false
      })
    } else {
      form.resetForm()
    }
  }, [isEditing, currentSkill, form])

  const handleSave = useCallback(async () => {
    if (!form.skillId || idInvalid) return
    if (!hasRequiredFields) {
      const missing = []
      if (!form.description.trim()) missing.push('Description')
      if (!form.useWhen.some(s => s.trim())) missing.push('Selection Triggers')
      if (!form.instructions.trim()) missing.push('Instructions')
      setToast({ severity: 'error', message: `Required: ${missing.join(', ')}` })
      return
    }
    setSaving(true)
    try {
      const skillContent = {
        description: form.description,
        useWhen: form.useWhen,
        dontUseWhen: form.dontUseWhen,
        instructions: form.instructions,
        defaultPrompt: form.defaultPrompt || undefined,
        greetingMessage: form.greetingMessage || undefined,
        iconName: form.skillConfig.iconName,
        focuses: form.skillConfig.focuses,
        audience: form.skillConfig.audience,
        modelId: form.skillConfig.modelId || undefined,
        maxIterations: form.skillConfig.maxIterations || undefined,
        temperature: form.skillConfig.temperature ?? undefined,
        maxTokens: form.skillConfig.maxTokens ?? undefined,
        visible: form.skillConfig.visible !== false
      }

      const result = await insertSkill.mutateAsync({
        skillId: form.skillId,
        skillName: form.skillName,
        skillContent
      })

      const newVersion = result?.skill?.version
      isDirtyRef.current = false
      if (!isEditing && form.skillId) {
        history.replace(history.location.pathname.replace(/\/new$/, `/${form.skillId}`))
      }
      if (newVersion) {
        setSelectedVersion(newVersion)
        hydrated.current = false
      }
      setToast({
        severity: 'success',
        message: result?.versionCreated ? `Saved as version ${newVersion}` : `Updated version ${newVersion}`
      })
    } catch (err) {
      setToast({ severity: 'error', message: err?.message || 'Failed to save' })
    } finally {
      setSaving(false)
    }
  }, [form, idInvalid, hasRequiredFields, isEditing, insertSkill, history])

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleSave])

  const lastSavedInfo = useMemo(() => {
    if (!currentSkill) return null
    const { modifiedBy, modifiedDate, createdBy, createdDate } = currentSkill
    const who = modifiedBy || createdBy || null
    const when = modifiedDate || createdDate || null
    if (!when) return null
    const ago = formatRelativeTime(when)
    return who ? `${who}, ${ago}` : ago
  }, [currentSkill])

  if (skillLoading && isEditing && !hydrated.current) {
    return <Loading />
  }

  return (
    <div className={classes.page}>
      <Prompt
        when={isDirty}
        message={() => {
          if (!isDirtyRef.current) return true
          return 'You have unsaved changes. Are you sure you want to leave?'
        }}
      />
      <div className={classes.headerBar}>
        <div className={classes.headerField} style={{ width: '160px' }}>
          <label className={classes.fieldLabel} style={idInvalid ? { color: 'var(--color-danger-500)' } : undefined}>ID</label>
          <input
            className={classes.fieldInput}
            value={form.skillId}
            onChange={(e) => form.setSkillId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder='skill-id'
            readOnly={isEditing}
            style={isEditing ? { backgroundColor: 'var(--color-neutral-050)', cursor: 'default' } : undefined}
          />
        </div>
        <div className={classes.headerField} style={{ minWidth: '160px' }}>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.fieldInput}
            value={form.skillName}
            onChange={(e) => form.setSkillName(e.target.value)}
            placeholder='Untitled Skill'
            style={{ width: '100%' }}
          />
        </div>
        <div className={classes.headerField} style={{ flex: 1 }}>
          <label className={classes.fieldLabel}>Example Prompt <span className={classes.fieldDescription}>Pre-fills for users who select this skill</span></label>
          <input
            className={classes.fieldInput}
            value={form.defaultPrompt}
            onChange={(e) => form.setDefaultPrompt(e.target.value)}
            placeholder='e.g. "Show me my portfolio summary"'
            maxLength={300}
            style={{ width: '100%' }}
          />
        </div>
        <div className={classes.headerField} style={{ flex: 1 }}>
          <Tooltip title={'Use {{userName}} to insert the user\'s first name'} arrow>
            <label className={classes.fieldLabel}>Greeting Message <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.7 }}>{'{{userName}}'}</span></label>
          </Tooltip>
          <textarea
            className={classes.fieldInput}
            value={form.greetingMessage}
            onChange={(e) => form.setGreetingMessage(e.target.value)}
            placeholder='e.g. "{{userName}}, I have put on my Analysis hat..."'
            maxLength={300}
            rows={1}
            autoComplete='off'
            data-form-type='other'
            data-lpignore='true'
            data-1p-ignore
            style={{ width: '100%', fontFamily: 'inherit', resize: 'none', overflow: 'hidden' }}
          />
        </div>

        <div className={classes.headerField}>
          <label className={classes.fieldLabel}>Version</label>
          <select
            className={classes.versionSelect}
            value={currentVersion}
            onChange={(e) => {
              const v = Number(e.target.value)
              setSelectedVersion(v)
              hydrated.current = false
            }}
          >
            {versions.map(v => (
              <option key={v} value={v}>v{v}</option>
            ))}
          </select>
        </div>

        <div className={classes.headerField}>
          {tagsForCurrentVersion.length > 0 && (
            <div className={classes.tagBadgeWrap}>
              {tagsForCurrentVersion.map(tag => (
                <Tooltip key={tag} title={tag === 'next' ? 'This is the latest version' : `Tag "${tag}" points to this version`} arrow>
                  <span className={`${classes.tagBadge} ${tag === 'live' ? classes.tagBadgeLive : ''}`}>
                    {tag}
                  </span>
                </Tooltip>
              ))}
            </div>
          )}
          <div className={classes.headerActions}>
            <div className={classes.configPopoverAnchor} ref={configRef}>
              <button
                className={`${classes.gearBtn} ${configOpen ? classes.gearBtnOpen : ''}`}
                onClick={() => setConfigOpen(prev => !prev)}
                title='Skill configuration'
                aria-label='Skill configuration'
              >
                <Icon name='settings' customSize='16px' />
              </button>
              {configOpen && (
                <SkillConfigPopover
                  skillConfig={form.skillConfig}
                  onConfigChange={form.setSkillConfig}
                  models={modelsData?.models}
                  onClose={() => setConfigOpen(false)}
                />
              )}
            </div>
            <SydButton size='sm' variant='secondary' onClick={handleReset} disabled={saving}>Reset</SydButton>
            <SydButton size='sm' variant='primary' icon='check' onClick={handleSave} disabled={!form.skillId || idInvalid || !hasRequiredFields || saving}>
              {saving ? 'Saving\u2026' : 'Save'}
            </SydButton>
          </div>
        </div>
      </div>

      <div className={classes.body}>
        <div className={`${classes.column} ${classes.leftColumn}`}>
          <SettingsPanel />
        </div>
        <div className={`${classes.column} ${classes.middleColumn}`}>
          <InstructionsPanel />
        </div>
        <div className={`${classes.column} ${classes.rightColumn}`}>
          <DesignerPanel />
        </div>
      </div>

      <div className={classes.statusBar}>
        <span>
          <span
            className={classes.statusDot}
            style={{ backgroundColor: isDirty ? 'var(--color-warning-400)' : 'var(--color-success-500)' }}
          />
          {isDirty ? 'Unsaved changes' : 'Saved'}
        </span>
        <span>Version {currentVersion || 1}</span>
        {lastSavedInfo && <span>Last saved {lastSavedInfo}</span>}
        <span style={{ marginLeft: 'auto' }}>
          AI Skill &middot; {form.skillId || 'no id'}
        </span>
      </div>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </div>
  )
}

function SkillWorkspace () {
  return (
    <SkillFormProvider>
      <SkillWorkspaceInner />
    </SkillFormProvider>
  )
}

export default SkillWorkspace
